/**
 * Google Analytics & Google Ads Conversion Tracking Utility
 * Tracks key conversion events for The Coppini Academy of Fine Art
 * 
 * GA4 ID: G-7SDRSSPLRY
 * Google Ads ID: AW-17744505172
 */

// Safe dynamic gtag proxy — delegates to window.gtag at call time, not import time.
// This avoids the race condition where window.gtag is undefined when the module
// is first imported (the async gtag.js script may not have loaded yet).
const gtag = (...args) => {
  if (window.__CAFA_DEVELOPMENT__) return;
  if (window.gtag) {
    window.gtag(...args);
  } else {
    console.warn('gtag not yet loaded, event queued:', args[0]);
  }
};

// Google Ads Conversion IDs
const CONVERSION_IDS = {
  // Primary CTA conversion tracking (AW-348671949)
  PRIMARY_CTA: 'AW-348671949',
  PURCHASE: 'AW-17744505172/S4_JCP20tOkbENTsno1C', // Workshop/Class purchase (dynamic value)
  NEWSLETTER_SIGNUP: 'AW-17744505172/hy6CICPtoKbENTsno1C', // Newsletter signup ($5 value)
  CONTACT_FORM: 'AW-17744505172/IGS_CI-ZtukbENTsno1C', // Contact form ($10 value)
};

// =============================================================================
// RADIO SOURCE ATTRIBUTION
// =============================================================================

/**
 * Store radio source in sessionStorage for attribution tracking
 * This persists across page navigations within the same session
 */
export const storeRadioSource = (source) => {
  if (source) {
    const radioData = {
      source: source,
      landingPage: window.location.pathname,
      timestamp: new Date().toISOString(),
      utmParams: getUTMParams()
    };
    sessionStorage.setItem('cafa_radio_source', JSON.stringify(radioData));
    console.log('Analytics: Radio source stored', radioData);
  }
};

/**
 * Get stored radio source from sessionStorage
 */
export const getRadioSource = () => {
  try {
    const data = sessionStorage.getItem('cafa_radio_source');
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Clear radio source (call after conversion is attributed)
 */
export const clearRadioSource = () => {
  sessionStorage.removeItem('cafa_radio_source');
};

/**
 * Get UTM parameters from current URL
 */
export const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term')
  };
};

/**
 * Detect radio source from UTM parameters or referrer
 */
export const detectRadioSource = () => {
  const utmParams = getUTMParams();
  const source = utmParams.utm_source?.toLowerCase();
  
  // Check for known radio sources
  if (source === 'kpac' || source === 'kpac_radio') return 'kpac_radio';
  if (source === 'kahl' || source === 'kahl_radio') return 'kahl_radio';
  if (source === 'radio') return 'radio_general';
  
  // Check utm_medium for radio
  if (utmParams.utm_medium?.toLowerCase() === 'radio') {
    return source || 'radio_general';
  }
  
  return null;
};

/**
 * Track Radio-Attributed Conversion
 * Call this when a conversion happens and radio source is stored
 */
export const trackRadioConversion = (conversionType, value = 0, details = {}) => {
  const radioSource = getRadioSource();
  
  if (radioSource) {
    gtag('event', 'radio_conversion', {
      event_category: 'radio_attribution',
      event_label: conversionType,
      radio_source: radioSource.source,
      conversion_type: conversionType,
      conversion_value: value,
      original_landing: radioSource.landingPage,
      time_to_convert: Math.round((Date.now() - new Date(radioSource.timestamp).getTime()) / 1000 / 60), // minutes
      ...details
    });

    console.log('Analytics: Radio conversion tracked', { 
      conversionType, 
      value, 
      radioSource: radioSource.source 
    });

    return radioSource;
  }
  
  return null;
};

/**
 * Track a page view (automatic with GA4, but useful for SPA navigation)
 */
export const trackPageView = (pagePath, pageTitle) => {
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * Track Newsletter Signup Conversion
 */
export const trackNewsletterSignup = (email, interests = []) => {
  // GA4 Event
  gtag('event', 'newsletter_signup', {
    event_category: 'engagement',
    event_label: 'Newsletter Subscription',
    interests: interests.join(', '),
  });

  // GA4 Generate Lead event (standard event)
  gtag('event', 'generate_lead', {
    currency: 'USD',
    value: 5, // Estimated lead value
    lead_source: 'newsletter_form',
  });

  // Google Ads Conversion
  gtag('event', 'conversion', {
    send_to: CONVERSION_IDS.NEWSLETTER_SIGNUP,
    value: 5.0,
    currency: 'USD',
  });

  console.log('Analytics: Newsletter signup tracked', { email: email?.substring(0, 3) + '***' });
};

/**
 * Track Contact Form Submission
 */
export const trackContactFormSubmit = (subject) => {
  // GA4 Event
  gtag('event', 'contact_form_submit', {
    event_category: 'engagement',
    event_label: subject || 'General Inquiry',
  });

  // GA4 Generate Lead event
  gtag('event', 'generate_lead', {
    currency: 'USD',
    value: 10,
    lead_source: 'contact_form',
  });

  // Google Ads Conversion
  gtag('event', 'conversion', {
    send_to: CONVERSION_IDS.CONTACT_FORM,
    value: 10.0,
    currency: 'USD',
  });

  console.log('Analytics: Contact form tracked', { subject });
};

/**
 * Track Purchase/Payment Completion
 * This is the most valuable conversion event
 */
export const trackPurchase = (transactionId, items, total, paymentMethod = 'PayPal') => {
  // GA4 Purchase Event (standard e-commerce event)
  gtag('event', 'purchase', {
    transaction_id: transactionId,
    value: total,
    currency: 'USD',
    payment_type: paymentMethod,
    items: items.map((item, index) => ({
      item_id: item.id || `item_${index}`,
      item_name: item.name,
      item_category: item.category || 'Workshop/Class',
      price: item.price,
      quantity: item.quantity || 1,
    })),
  });

  // Google Ads Conversion
  gtag('event', 'conversion', {
    send_to: CONVERSION_IDS.PURCHASE,
    value: total,
    currency: 'USD',
    transaction_id: transactionId,
  });

  console.log('Analytics: Purchase tracked', { transactionId, total, itemCount: items.length });
};

/**
 * Track Workshop Registration Start (begin checkout)
 */
export const trackBeginCheckout = (items, total) => {
  gtag('event', 'begin_checkout', {
    currency: 'USD',
    value: total,
    items: items.map((item, index) => ({
      item_id: item.id || `item_${index}`,
      item_name: item.name,
      item_category: item.category || 'Workshop/Class',
      price: item.price,
      quantity: item.quantity || 1,
    })),
  });

  console.log('Analytics: Begin checkout tracked', { total, itemCount: items.length });
};

/**
 * Track Class/Workshop View
 */
export const trackViewItem = (item) => {
  gtag('event', 'view_item', {
    currency: 'USD',
    value: item.price,
    items: [{
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Workshop/Class',
      price: item.price,
    }],
  });

  console.log('Analytics: View item tracked', { itemName: item.name });
};

/**
 * Track Membership Inquiry / Page View
 */
export const trackMembershipInquiry = (membershipType) => {
  gtag('event', 'membership_inquiry', {
    event_category: 'engagement',
    event_label: membershipType || 'General',
  });

  // GA4 generate_lead for membership inquiries
  gtag('event', 'generate_lead', {
    currency: 'USD',
    value: 25,
    lead_source: 'membership_inquiry',
  });

  console.log('Analytics: Membership inquiry tracked', { membershipType });
};

/**
 * Track Class Registration Completion
 * Note: For paid registrations, use trackPurchase instead
 */
export const trackClassRegistration = (className, studentType, price) => {
  gtag('event', 'class_registration', {
    event_category: 'conversion',
    event_label: className,
    student_type: studentType,
    value: price,
  });

  // Use Purchase conversion for paid class registrations
  if (price > 0) {
    gtag('event', 'conversion', {
      send_to: CONVERSION_IDS.PURCHASE,
      value: price,
      currency: 'USD',
    });
  }

  console.log('Analytics: Class registration tracked', { className, price });
};

/**
 * Track User Login
 */
export const trackLogin = (method = 'email') => {
  gtag('event', 'login', {
    method: method,
  });

  console.log('Analytics: Login tracked');
};

/**
 * Track User Registration/Signup
 */
export const trackSignUp = (method = 'email') => {
  gtag('event', 'sign_up', {
    method: method,
  });

  console.log('Analytics: Sign up tracked');
};

/**
 * Track Search
 */
export const trackSearch = (searchTerm) => {
  gtag('event', 'search', {
    search_term: searchTerm,
  });
};

/**
 * Track Qualified Lead — GA4 funnel step 2
 * Fire when a user initiates PayPal checkout (Proceed to Payment) on the membership page
 */
export const trackQualifyLead = ({ membership_type, value, currency = 'USD' } = {}) => {
  if (!window.__CAFA_DEVELOPMENT__ && window.gtag) {
    window.gtag('event', 'qualify_lead', {
      send_to: 'G-7SDRSSPLRY',
      membership_type: membership_type || 'Unknown',
      value: value,
      currency: currency
    });
  }
  console.log('Analytics: qualify_lead tracked', { membership_type, value });
};

/**
 * Track Converted Lead — GA4 funnel step 3
 * Fire when PayPal onApprove succeeds (payment captured)
 */
export const trackCloseLead = ({ membership_type, value, currency = 'USD', transaction_id } = {}) => {
  if (!window.__CAFA_DEVELOPMENT__ && window.gtag) {
    window.gtag('event', 'close_convert_lead', {
      send_to: 'G-7SDRSSPLRY',
      membership_type: membership_type || 'Unknown',
      value: value,
      currency: currency,
      transaction_id: transaction_id
    });
  }
  console.log('Analytics: close_convert_lead tracked', { membership_type, value, transaction_id });
};

/**
 * Track Checkout Abandonment
 * Fire when a user starts PayPal checkout but cancels without completing payment
 */
export const trackCheckoutAbandonment = () => {
  if (!window.__CAFA_DEVELOPMENT__ && window.gtag) {
    window.gtag('event', 'checkout_abandonment', {
      event_category: 'Workshop Registration',
      event_label: 'PayPal Abandoned'
    });
  }
  console.log('Analytics: Checkout abandonment tracked');
};

/**
 * Track Primary CTA Conversion (AW-348671949)
 * Fire on: Join Now, Explore Classes, Take a Workshop, Become a Member, contact/form submissions
 * Optionally pass value, currency, transaction_id for richer purchase events (e.g. PayPal success)
 */
export const trackAdsConversion = ({ value, currency = 'USD', transaction_id } = {}) => {
  if (!window.__CAFA_DEVELOPMENT__ && window.gtag) {
    const payload = { send_to: 'AW-348671949' };
    // Only include value/currency/transaction_id when value is a real number
    if (typeof value === 'number' && !isNaN(value)) {
      payload.value = value;
      payload.currency = currency;
    }
    if (transaction_id) payload.transaction_id = transaction_id;
    window.gtag('event', 'conversion', payload);
  }
  console.log('Analytics: Google Ads conversion tracked (AW-348671949)', { value, transaction_id });
};

/**
 * Track Button Click (generic event tracking)
 */
export const trackButtonClick = (buttonName, category = 'engagement') => {
  gtag('event', 'button_click', {
    event_category: category,
    event_label: buttonName,
  });
};

/**
 * Track Outbound Link Click
 */
export const trackOutboundLink = (url, linkText) => {
  gtag('event', 'click', {
    event_category: 'outbound',
    event_label: url,
    link_text: linkText,
    transport_type: 'beacon',
  });
};

export default {
  trackPageView,
  trackNewsletterSignup,
  trackContactFormSubmit,
  trackPurchase,
  trackBeginCheckout,
  trackViewItem,
  trackMembershipInquiry,
  trackClassRegistration,
  trackLogin,
  trackSignUp,
  trackSearch,
  trackButtonClick,
  trackOutboundLink,
  trackAdsConversion,
  trackCheckoutAbandonment,
  trackQualifyLead,
  trackCloseLead,
  // Radio Attribution
  trackRadioConversion,
  storeRadioSource,
  getRadioSource,
  clearRadioSource,
  detectRadioSource,
  getUTMParams,
};
